"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
const stages = [{
  title: "Asset Audit",
  description: "A comprehensive review of your flight deck: home equity, aircraft valuation, and retirement reserves to fuel your lifetime care plan.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_instrument_audit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_instrument_audit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_instrument_audit.png-wvc-sizes",
    alt: "Close-up of a technical aircraft instrument panel with high precision"
  }
}, {
  title: "Financial Engineering",
  description: "We structure a custom flight plan that converts assets into guaranteed in-home care while maintaining your connection to aviation.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/flight_path_engineering.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/flight_path_engineering.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/flight_path_engineering.png-wvc-sizes",
    alt: "Technical schematic of a flight path over a dark navy background"
  }
}, {
  title: "Care Launch",
  description: "Engage your dedicated Care Advisor and transition to a structured home-based arrangement with right-seat flying opportunities.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_dawn_launch.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_dawn_launch.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_dawn_launch.png-wvc-sizes",
    alt: "A runway at dawn with lights guiding the way for a smooth takeoff"
  }
}];
export default function OperationalLogistics3Steps() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollable = rect.height - windowHeight;
      if (totalScrollable <= 0) return;
      let currentProgress = -rect.top / totalScrollable;
      currentProgress = Math.max(0, Math.min(1, currentProgress));
      setProgress(currentProgress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const activeIndex = Math.min(2, Math.floor(progress * 3));
  return <section data-section-id="262092" id="operational-logistics-3-steps" ref={sectionRef} className="bg-background text-foreground h-[300vh] relative">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden py-12 px-4 md:px-8 lg:px-16">
        
        <div className="text-center mb-12 md:mb-20 z-10">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tighter uppercase">
            The Operational Logistics
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg md:text-xl font-mono uppercase tracking-wider">
            A structured flight plan for aging in place.
          </p>
        </div>

        <div className="w-full max-w-4xl relative mb-16 md:mb-24 z-10 px-4 md:px-12">
          <div className="absolute top-1/2 left-4 right-4 md:left-12 md:right-12 h-[1px] bg-border -translate-y-1/2"></div>
          
          <div className="absolute top-1/2 left-4 md:left-12 h-[2px] bg-primary -translate-y-1/2 transition-all duration-75 ease-linear" style={{
          width: `calc(${progress * 100}% - 2rem)`
        }}></div>

          <div className="relative flex justify-between items-center w-full">
            {stages.map((stage, i) => {
            const isActive = i <= activeIndex;
            const isCurrent = i === activeIndex;
            return <div key={i} data-index={i} className="flex flex-col items-center relative">
                  <div className={`w-3 h-3 md:w-4 md:h-4 rounded-md border-2 transition-all duration-500 z-10 ${isActive ? "bg-primary border-primary" : "bg-background border-border"} ${isCurrent ? "shadow-[0_0_15px_rgba(251,84,13,0.6)] scale-125" : "scale-100"}`}></div>
                  
                  <span className={`absolute top-8 font-default text-xs md:text-sm font-bold uppercase tracking-widest transition-colors duration-500 ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                    {stage.title}
                  </span>
                </div>;
          })}
          </div>

          <div className="absolute top-1/2 w-5 h-5 md:w-6 md:h-6 bg-primary rounded-md -translate-y-1/2 -translate-x-1/2 z-20 transition-all duration-75 ease-linear shadow-lg" style={{
          left: `calc(1rem + ${progress * 100}% * 0.9)`
        }}>
            <div className="absolute inset-1 bg-background rounded-sm opacity-30"></div>
          </div>
        </div>

        <div className="relative w-full max-w-6xl h-[450px] md:h-[500px] lg:h-[550px]">
          {stages.map((stage, i) => {
          const isCurrent = i === activeIndex;
          return <div key={i} data-index={i} className={`absolute inset-0 transition-all duration-700 ease-in-out flex flex-col md:flex-row items-stretch ${isCurrent ? "opacity-100 translate-y-0 pointer-events-auto z-10" : "opacity-0 translate-y-12 pointer-events-none z-0"}`}>
                <div className="w-full md:w-1/2 h-64 md:h-full relative overflow-hidden border border-border/40 bg-card/10 backdrop-blur-sm p-2 md:p-4">
                  <div className="w-full h-full relative overflow-hidden border border-border/20 rounded-md">
                    <img src={stage.image.src} data-wvc-srcset={stage.image.srcset} data-wvc-sizes={stage.image.sizes} alt={stage.image.alt} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-[2000ms] ease-out hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent md:hidden"></div>
                  </div>
                </div>

                <div className="w-full md:w-1/2 flex flex-col justify-center p-8 md:p-12 lg:p-16 bg-card/5 backdrop-blur-md border border-border/30 border-t-0 md:border-t md:border-l-0 h-full relative overflow-hidden rounded-md">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-md blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                  
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
                    <span className="font-default text-[12rem] font-bold text-foreground border-foreground/20">
                      0{i + 1}
                    </span>
                  </div>

                  <div className="relative z-10">
                    <span className="text-xs md:text-sm uppercase tracking-widest text-primary font-mono mb-4 block">
                      Checklist Item 0{i + 1}
                    </span>
                    <h3 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground uppercase tracking-tight">
                      {stage.title}
                    </h3>
                    <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 font-default">
                      {stage.description}
                    </p>
                    
                    {i === 2 && <div className="mt-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300 fill-mode-both">
                        <Button size="lg" className="rounded-md px-8 text-base tracking-widest uppercase font-bold bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">
                          Schedule a Free Consultation
                        </Button>
                      </div>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}